'use client'

/**
 * FilterPanel Component
 *
 * A comprehensive, accessible filter panel with collapsible sections,
 * active filter chips, and support for various filter types.
 *
 * Features:
 * - Collapsible sections with smooth animations
 * - Active filter chips with clear buttons
 * - Search within multi-select options
 * - Number range inputs with unit labels
 * - Share URL functionality
 * - Full RTL support
 *
 * @example
 * ```tsx
 * <FilterPanel
 *   title={t('common.filters')}
 *   sections={[
 *     {
 *       id: 'status',
 *       title: t('salesOrders.filterByStatus'),
 *       children: (
 *         <FilterCheckboxGroup
 *           options={statusOptions}
 *           selected={filter.status}
 *           onChange={(values) => setFilter({ status: values })}
 *         />
 *       ),
 *     },
 *   ]}
 *   activeFilterCount={3}
 *   onApply={handleApply}
 *   onClear={handleClear}
 *   onShare={handleShare}
 * />
 * ```
 */

import * as React from 'react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { Input } from './input'
import { Label } from './label'
import { Checkbox } from './checkbox'
import { Badge } from './badge'
import { ScrollArea } from './scroll-area'
import { Separator } from './separator'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './collapsible'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetTrigger,
} from './sheet'
import {
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Search,
  Share2,
  Check,
  SlidersHorizontal,
} from 'lucide-react'

// ============================================
// Types
// ============================================

export interface FilterPanelSection {
  /** Unique section identifier */
  id: string
  /** Section title */
  title: string
  /** Section description */
  description?: string
  /** Whether section is collapsible (default: true) */
  collapsible?: boolean
  /** Whether section starts collapsed (default: false) */
  defaultCollapsed?: boolean
  /** Section content */
  children: React.ReactNode
  /** Badge count (number of active filters in section) */
  activeCount?: number
}

export interface FilterChip {
  /** Filter group ID */
  groupId: string
  /** Group label */
  groupLabel: string
  /** Filter value */
  value: string
  /** Display label */
  label: string
}

export interface FilterPanelProps {
  /** Panel title */
  title?: string
  /** Panel description */
  description?: string
  /** Filter sections */
  sections: FilterPanelSection[]
  /** Number of active filters (for badge) */
  activeFilterCount?: number
  /** Active filter chips to display */
  activeChips?: FilterChip[]
  /** Handler for clearing a single filter */
  onClearChip?: (groupId: string, value: string) => void
  /** Handler for applying filters */
  onApply?: () => void
  /** Handler for clearing all filters */
  onClear?: () => void
  /** Handler for sharing filter URL */
  onShare?: () => void
  /** Additional class name */
  className?: string
  /** Is RTL mode */
  isRTL?: boolean
  /** Trigger button label */
  triggerLabel?: string
  /** Show apply/clear buttons (default: false for instant apply) */
  showActionButtons?: boolean
  /** Apply button label */
  applyLabel?: string
  /** Clear button label */
  clearLabel?: string
  /** Share button label */
  shareLabel?: string
  /** Mode: 'sheet' for slide-out panel, 'inline' for embedded */
  mode?: 'sheet' | 'inline'
  /** Sheet side (for sheet mode) */
  sheetSide?: 'left' | 'right'
}

// ============================================
// Sub-Components
// ============================================

/**
 * Collapsible filter section
 */
function FilterSection({
  section,
  isRTL = false,
}: {
  section: FilterPanelSection
  isRTL?: boolean
}) {
  const [isOpen, setIsOpen] = React.useState(!section.defaultCollapsed)
  const collapsible = section.collapsible !== false

  if (!collapsible) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{section.title}</Label>
          {section.activeCount !== undefined && section.activeCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {section.activeCount}
            </Badge>
          )}
        </div>
        {section.description && (
          <p className="text-xs text-muted-foreground">{section.description}</p>
        )}
        {section.children}
      </div>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between py-2 text-sm font-medium hover:text-foreground transition-colors"
        >
          <span className="flex items-center gap-2">
            {section.title}
            {section.activeCount !== undefined && section.activeCount > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {section.activeCount}
              </Badge>
            )}
          </span>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-2">
        {section.description && (
          <p className="text-xs text-muted-foreground">{section.description}</p>
        )}
        {section.children}
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Active filter chips display
 */
function ActiveFilterChips({
  chips,
  onClearChip,
  onClearAll,
  isRTL = false,
  clearAllLabel = 'Clear all',
}: {
  chips: FilterChip[]
  onClearChip?: (groupId: string, value: string) => void
  onClearAll?: () => void
  isRTL?: boolean
  clearAllLabel?: string
}) {
  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        <Button
          key={`${chip.groupId}-${chip.value}`}
          variant="secondary"
          size="sm"
          className="h-7 px-2 rounded-full text-xs gap-1 hover:bg-destructive/10 hover:text-destructive"
          onClick={() => onClearChip?.(chip.groupId, chip.value)}
        >
          <span className="text-muted-foreground">{chip.groupLabel}:</span>
          <span>{chip.label}</span>
          <X className="w-3 h-3 ms-1" />
        </Button>
      ))}
      {chips.length > 1 && onClearAll && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 rounded-full text-xs text-muted-foreground hover:text-destructive"
          onClick={onClearAll}
        >
          {clearAllLabel}
        </Button>
      )}
    </div>
  )
}

// ============================================
// Filter Panel Content
// ============================================

function FilterPanelContent({
  sections,
  activeChips,
  onClearChip,
  onClear,
  isRTL = false,
  clearAllLabel = 'Clear all',
}: {
  sections: FilterPanelSection[]
  activeChips?: FilterChip[]
  onClearChip?: (groupId: string, value: string) => void
  onClear?: () => void
  isRTL?: boolean
  clearAllLabel?: string
}) {
  return (
    <div className="space-y-4">
      {/* Active filter chips */}
      {activeChips && activeChips.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">
            Active Filters
          </Label>
          <ActiveFilterChips
            chips={activeChips}
            onClearChip={onClearChip}
            onClearAll={onClear}
            isRTL={isRTL}
            clearAllLabel={clearAllLabel}
          />
          <Separator className="my-3" />
        </div>
      )}

      {/* Filter sections */}
      <div className="space-y-4">
        {sections.map((section, index) => (
          <React.Fragment key={section.id}>
            <FilterSection section={section} isRTL={isRTL} />
            {index < sections.length - 1 && <Separator />}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function FilterPanel({
  title = 'Filters',
  description,
  sections,
  activeFilterCount = 0,
  activeChips,
  onClearChip,
  onApply,
  onClear,
  onShare,
  className,
  isRTL = false,
  triggerLabel = 'Filters',
  showActionButtons = false,
  applyLabel = 'Apply',
  clearLabel = 'Clear all',
  shareLabel = 'Share',
  mode = 'sheet',
  sheetSide,
}: FilterPanelProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  // Determine sheet side based on RTL
  const side = sheetSide ?? (isRTL ? 'left' : 'right')

  const handleApply = () => {
    onApply?.()
    if (mode === 'sheet') {
      setIsOpen(false)
    }
  }

  const handleClear = () => {
    onClear?.()
  }

  // Inline mode
  if (mode === 'inline') {
    return (
      <div className={cn('rounded-lg border bg-card p-4', className)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            {title}
            {activeFilterCount > 0 && (
              <Badge variant="default" className="h-5 px-1.5 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {onShare && (
              <Button variant="ghost" size="sm" onClick={onShare}>
                <Share2 className="h-4 w-4" />
              </Button>
            )}
            {onClear && activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClear}>
                {clearLabel}
              </Button>
            )}
          </div>
        </div>
        {description && (
          <p className="text-sm text-muted-foreground mb-4">{description}</p>
        )}
        <ScrollArea className="h-auto max-h-[60vh]">
          <FilterPanelContent
            sections={sections}
            activeChips={activeChips}
            onClearChip={onClearChip}
            onClear={onClear}
            isRTL={isRTL}
            clearAllLabel={clearLabel}
          />
        </ScrollArea>
        {showActionButtons && (
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={handleClear}>
              {clearLabel}
            </Button>
            <Button onClick={handleApply}>
              {applyLabel}
            </Button>
          </div>
        )}
      </div>
    )
  }

  // Sheet mode
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="default" className={cn('gap-2', className)}>
          <Filter className="h-4 w-4" />
          {triggerLabel}
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side={side} className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5" />
            {title}
            {activeFilterCount > 0 && (
              <Badge variant="default" className="h-5 px-1.5 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </SheetTitle>
          {description && (
            <SheetDescription>{description}</SheetDescription>
          )}
        </SheetHeader>

        <ScrollArea className="flex-1 py-4 -mx-6 px-6">
          <FilterPanelContent
            sections={sections}
            activeChips={activeChips}
            onClearChip={onClearChip}
            onClear={onClear}
            isRTL={isRTL}
            clearAllLabel={clearLabel}
          />
        </ScrollArea>

        <SheetFooter className="flex-row gap-2 pt-4 border-t">
          {onShare && (
            <Button variant="ghost" size="icon" onClick={onShare} title={shareLabel}>
              <Share2 className="h-4 w-4" />
            </Button>
          )}
          <div className="flex-1" />
          {onClear && (
            <Button variant="outline" onClick={handleClear}>
              {clearLabel}
            </Button>
          )}
          {showActionButtons && onApply && (
            <Button onClick={handleApply}>
              <Check className="h-4 w-4 me-2" />
              {applyLabel}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

// ============================================
// Filter Helper Components
// ============================================

export interface FilterCheckboxGroupProps {
  /** Available options */
  options: Array<{
    value: string
    label: string
    description?: string
    color?: string
    icon?: React.ReactNode
  }>
  /** Currently selected values */
  selected: string[]
  /** Change handler */
  onChange: (values: string[]) => void
  /** Show search input (for many options) */
  searchable?: boolean
  /** Search placeholder */
  searchPlaceholder?: string
  /** Maximum height (scrollable) */
  maxHeight?: number
  /** Is RTL mode */
  isRTL?: boolean
}

/**
 * Checkbox group for multi-select filters
 */
export function FilterCheckboxGroup({
  options,
  selected,
  onChange,
  searchable = false,
  searchPlaceholder = 'Search...',
  maxHeight = 200,
  isRTL = false,
}: FilterCheckboxGroupProps) {
  const [search, setSearch] = React.useState('')

  const filteredOptions = React.useMemo(() => {
    if (!search) return options
    const searchLower = search.toLowerCase()
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(searchLower) ||
        opt.description?.toLowerCase().includes(searchLower)
    )
  }, [options, search])

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div className="space-y-2">
      {searchable && (
        <div className="relative">
          <Search className={cn(
            'absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground',
            isRTL ? 'end-3' : 'start-3'
          )} />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn('h-8 text-sm', isRTL ? 'pe-9' : 'ps-9')}
          />
        </div>
      )}
      <div
        className="space-y-1 overflow-y-auto"
        style={{ maxHeight: maxHeight > 0 ? maxHeight : undefined }}
      >
        {filteredOptions.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors',
              selected.includes(option.value) && 'bg-muted'
            )}
          >
            <Checkbox
              checked={selected.includes(option.value)}
              onCheckedChange={() => handleToggle(option.value)}
            />
            <div className={cn('flex-1 min-w-0', isRTL && 'text-end')}>
              <span className="text-sm flex items-center gap-1.5">
                {option.icon}
                {option.color && (
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: option.color }}
                  />
                )}
                {option.label}
              </span>
              {option.description && (
                <span className="text-xs text-muted-foreground block">
                  {option.description}
                </span>
              )}
            </div>
          </label>
        ))}
        {filteredOptions.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No options found
          </p>
        )}
      </div>
    </div>
  )
}

export interface NumberRangeInputProps {
  /** Minimum value */
  minValue?: number
  /** Maximum value */
  maxValue?: number
  /** Min value change handler */
  onMinChange: (value: number | undefined) => void
  /** Max value change handler */
  onMaxChange: (value: number | undefined) => void
  /** Unit label (e.g., "SAR", "tons") */
  unit?: string
  /** Min label */
  minLabel?: string
  /** Max label */
  maxLabel?: string
  /** Placeholder for min input */
  minPlaceholder?: string
  /** Placeholder for max input */
  maxPlaceholder?: string
  /** Step increment */
  step?: number
  /** Is RTL mode */
  isRTL?: boolean
}

/**
 * Number range input for min/max filtering
 */
export function NumberRangeInput({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  unit,
  minLabel = 'Min',
  maxLabel = 'Max',
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
  step = 1,
  isRTL = false,
}: NumberRangeInputProps) {
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '') {
      onMinChange(undefined)
    } else {
      const num = Number(value)
      if (!Number.isNaN(num)) {
        onMinChange(num)
      }
    }
  }

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '') {
      onMaxChange(undefined)
    } else {
      const num = Number(value)
      if (!Number.isNaN(num)) {
        onMaxChange(num)
      }
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 space-y-1">
        <Label className="text-xs text-muted-foreground">{minLabel}</Label>
        <div className="relative">
          <Input
            type="number"
            value={minValue ?? ''}
            onChange={handleMinChange}
            placeholder={minPlaceholder}
            step={step}
            className={cn('h-8 text-sm', unit && (isRTL ? 'ps-12' : 'pe-12'))}
          />
          {unit && (
            <span className={cn(
              'absolute top-1/2 -translate-y-1/2 text-xs text-muted-foreground',
              isRTL ? 'start-3' : 'end-3'
            )}>
              {unit}
            </span>
          )}
        </div>
      </div>
      <span className="text-muted-foreground mt-5">–</span>
      <div className="flex-1 space-y-1">
        <Label className="text-xs text-muted-foreground">{maxLabel}</Label>
        <div className="relative">
          <Input
            type="number"
            value={maxValue ?? ''}
            onChange={handleMaxChange}
            placeholder={maxPlaceholder}
            step={step}
            className={cn('h-8 text-sm', unit && (isRTL ? 'ps-12' : 'pe-12'))}
          />
          {unit && (
            <span className={cn(
              'absolute top-1/2 -translate-y-1/2 text-xs text-muted-foreground',
              isRTL ? 'start-3' : 'end-3'
            )}>
              {unit}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
